import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { hasPermission } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { BANKING_DATA_QUERY_ROOTS } from '@/features/banking/bankingQueryInvalidation';
import { isSocketReady, releaseSocket, retainSocket } from '@/services/wsClient';
import type { RealtimeEventItem } from '@/services/realtimeClient';

const BANKING_ROOTS = [...BANKING_DATA_QUERY_ROOTS, 'dashboard'];
const TOPICS: Record<string, readonly string[]> = {
  'finance.actual_costs': [
    'finance',
    'accounting',
    'dashboard',
    'projects',
    'banking',
    'analytics',
    'reporting',
  ],
  'projects.projects': ['projects', 'dashboard', 'realtime', 'procurement'],
  'procurement.vendors': ['procurement', 'dashboard'],
  'crm.client_accounts': ['crm', 'dashboard'],
  'accounting.entity_links': [
    'accounting',
    'crm',
    'procurement',
    'finance',
    'projects',
    'dashboard',
  ],
  'accounting.sync_runs': ['accounting', 'dashboard', 'crm', 'procurement', 'finance', 'projects'],
  'documents.documents': ['documents', 'dashboard'],
  'documents.assets': ['documents', 'dashboard'],
  'documents.rfis': ['documents', 'rfis', 'projects', 'dashboard'],
  'documents.submittals': ['documents', 'submittals', 'projects', 'dashboard'],
  'banking.connections': BANKING_ROOTS,
  'banking.accounts': BANKING_ROOTS,
  'banking.transactions': BANKING_ROOTS,
  'banking.sync_runs': BANKING_ROOTS,
};

/** Shared shell subscription keeps vendor-backed views fresh across page navigation. */
export function useVendorDataRealtime(): void {
  const client = useQueryClient();
  const auth = useAuth();
  const tenantId = auth.tenant?.id ?? auth.user?.tenantId;
  const userId = auth.user?.id;
  const topicKey = Object.keys(TOPICS)
    .filter((topic) => hasPermission(auth.permissions, `${topic.replace('.', ':')}:read`))
    .join(',');
  useEffect(() => {
    if (!auth.authenticated || !tenantId || !userId || !topicKey) return;
    const topics = topicKey.split(',');
    const socket = retainSocket();
    let active = true;
    const invalidate = (roots: readonly string[]) => {
      if (!active) return;
      void client.invalidateQueries({
        predicate: (query) => roots.includes(String(query.queryKey[0])),
        refetchType: 'active',
      });
    };
    const ready = () => {
      if (!active) return;
      topics.forEach((topic) => socket.emit('realtime.subscribe', { topic }));
      invalidate([...new Set(topics.flatMap((topic) => TOPICS[topic] ?? []))]);
    };
    const receive = (event: RealtimeEventItem) => {
      if (
        !active ||
        !event?.id ||
        event.tenantId !== tenantId ||
        (event.recipientUserId !== null && event.recipientUserId !== userId) ||
        !topics.includes(event.topic)
      )
        return;
      invalidate(TOPICS[event.topic] ?? []);
    };
    socket.on('realtime.connected', ready);
    socket.on('realtime.event', receive);
    const polling = setInterval(() => {
      if (!isSocketReady())
        invalidate([...new Set(topics.flatMap((topic) => TOPICS[topic] ?? []))]);
    }, 60_000);
    if (isSocketReady()) ready();
    return () => {
      active = false;
      clearInterval(polling);
      socket.off('realtime.connected', ready);
      socket.off('realtime.event', receive);
      releaseSocket();
    };
  }, [client, auth.authenticated, tenantId, userId, topicKey]);
}
